import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CommunityDTO } from '../../../../DTO/CommunityDTO';
import { CustomFieldDTO } from '../../../../DTO/CustomFieldDTO';
import { ReservationDTO } from '../../../../DTO/PriorAppointment/ReservationDTO';
import { ServiceDTO } from '../../../../DTO/PriorAppointment/ServiceDTO';
import { ApiService } from '../../../../services/api.service';
import { ThemeService } from '../../../../services/theme/theme.service';

@Component({
  selector: 'app-reservation-step2',
  templateUrl: './reservation-step2.component.html',
  styleUrls: ['./reservation-step2.component.scss'],
})
export class ReservationStep2Component implements OnInit, OnChanges {
  @Input() nextAttempt = new EventEmitter<void>();
  @Output() formValid = new EventEmitter<boolean>();
  @Output() priceChanged = new EventEmitter<void>();
  @Input() public reservation: ReservationDTO;
  @Input() public community: CommunityDTO;
  @Input() communityModuleId: number | null = null;
  @Input() isPreparingForm = false;
  @Input() preReservationLabels = false;

  form: FormGroup;
  submitAttempted = false;
  holdLeftSeconds: number | null = null;
  /** Same idea as `reservation-form` in the app: load center + service for custom fields. */
  additionalDataLoading = false;
  mergedCustomFields: CustomFieldDTO[] = [];
  readonly additionalDataSkeletonRows = [0, 1, 2];

  documentTypes = [
    { label: 'CIF', value: 0 },
    { label: 'NIE', value: 1 },
    { label: 'DNI', value: 2 },
  ];

  private accesCode: string | null = null;

  constructor(
    public fb: FormBuilder,
    public _themeService: ThemeService,
    private apiService: ApiService,
    private actRoute: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.accesCode = this.actRoute.snapshot.queryParamMap.get('accesCode');
    this.initializeForm();
    this.refreshHoldLeftSeconds();
    void this.loadCustomFieldsFromApi();
    this.nextAttempt.subscribe(() => this.validateForm());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reservation']) {
      this.refreshHoldLeftSeconds();
    }
  }

  private refreshHoldLeftSeconds(): void {
    const untilIso = this.reservation?.reservedUntil;
    if (!untilIso) {
      this.holdLeftSeconds = null;
      return;
    }
    const untilMs = new Date(untilIso).getTime();
    if (!Number.isFinite(untilMs)) {
      this.holdLeftSeconds = null;
      return;
    }
    const seconds = Math.floor((untilMs - Date.now()) / 1000);
    this.holdLeftSeconds = seconds > 0 ? seconds : 0;
  }

  /**
   * Always rebuild fields from center + current service (`resourceId` or `serviceId`),
   * like `loadCustomFieldsFromApi` in the app reservation form.
   */
  private async loadCustomFieldsFromApi(): Promise<void> {
    const code = this.community?.code;
    const centerId = this.reservation?.centerId;
    const serviceId = this.reservation?.resourceId ?? this.reservation?.serviceId;
    if (!code || centerId == null || serviceId == null) {
      this.mergedCustomFields = [...(this.reservation?.customFields ?? [])];
      return;
    }

    this.additionalDataLoading = true;
    try {
      const [center, service] = await Promise.all([
        firstValueFrom(
          this.apiService.getCenterDetail(code, centerId, this.communityModuleId, this.accesCode),
        ),
        firstValueFrom(
          this.apiService.getServiceDetail(code, serviceId, this.communityModuleId, this.accesCode),
        ),
      ]);
      const list = this.mergeCenterAndServiceCustomFields(center, service);
      this.pruneCustomFieldValues(new Set(list.map((cf) => cf.id)));
      this.mergedCustomFields = list;
      this.reservation.customFields = list;
    } catch {
      this.mergedCustomFields = [...(this.reservation?.customFields ?? [])];
    } finally {
      this.additionalDataLoading = false;
    }
  }

  private mergeCenterAndServiceCustomFields(center: unknown, service: unknown): CustomFieldDTO[] {
    const centerFields: CustomFieldDTO[] = (center as { customFields?: CustomFieldDTO[] })?.customFields || [];
    const serviceFields: CustomFieldDTO[] =
      (service as ServiceDTO & { customFields?: CustomFieldDTO[] })?.customFields || [];
    const allFields = [...centerFields, ...serviceFields];
    const uniqueById = new Map<number, CustomFieldDTO>();
    allFields.forEach((cf) => {
      if (cf && typeof cf.id === 'number' && !uniqueById.has(cf.id)) {
        uniqueById.set(cf.id, cf);
      }
    });
    return Array.from(uniqueById.values());
  }

  private pruneCustomFieldValues(validFieldIds: Set<number>): void {
    const values = this.reservation?.customFieldValues;
    if (!values?.length) {
      return;
    }
    this.reservation.customFieldValues = values.filter(
      (v) => v?.customFieldId != null && validFieldIds.has(v.customFieldId),
    );
  }

  private initializeForm(): void {
    this.form = this.fb.group({
      name: [this.reservation?.name ?? '', Validators.required],
      surname: [this.reservation?.surname ?? '', Validators.required],
      phone: [this.reservation?.phone ?? '', [Validators.required]],
      email: [this.reservation?.mail ?? '', [Validators.required, Validators.email]],
      documentType: [this.reservation?.documentType ?? 2, Validators.required],
      documentNumber: [
        this.reservation?.numberDocumentation ?? '',
        [
          Validators.required,
          Validators.pattern(
            /^(?:(?:[XYZxyz][0-9]{7}[A-Za-z])|(?:[A-Za-z]\d{7}[0-9A-Ja-j])|(?:\d{8}[A-Za-z]))$/,
          ),
        ],
      ],
      observations: [this.reservation?.observations ?? ''],
      acceptTractament: [!!this.reservation?.acceptTractament, Validators.requiredTrue],
    });
    this.form.valueChanges.subscribe(() => {});
  }

  validateForm(): void {
    this.submitAttempted = true;
    this.form.markAllAsTouched();
    if (this.additionalDataLoading) {
      return;
    }
    if (this.form.valid) {
      const v = this.form.getRawValue();
      this.reservation.name = v.name;
      this.reservation.surname = v.surname;
      this.reservation.phone = v.phone;
      this.reservation.mail = v.email;
      this.reservation.documentType = v.documentType;
      this.reservation.numberDocumentation = v.documentNumber;
      this.reservation.observations = v.observations;
      this.reservation.acceptTractament = new Date();
      const fields = this.mergedCustomFields?.length ? this.mergedCustomFields : this.reservation?.customFields || [];
      if (fields.length) {
        this.reservation.customFieldValues = fields.map((cf) => {
          const control = this.form.get(cf.id.toString());
          return {
            customFieldId: cf.id,
            value: control ? (control.value ?? '').toString() : '',
          };
        });
      }
      this.formValid.emit(true);
    }
  }

  onCustomFieldsPriceChanged(): void {
    const fields = this.mergedCustomFields?.length ? this.mergedCustomFields : this.reservation?.customFields || [];
    if (fields.length) {
      this.reservation.customFieldValues = fields.map((cf) => {
        const control = this.form.get(cf.id.toString());
        return {
          customFieldId: cf.id,
          value: control ? (control.value ?? '').toString() : '',
        };
      });
    }
    this.applyExtrasAmountToReservationTotal();
    this.priceChanged.emit();
  }

  private applyExtrasAmountToReservationTotal(): void {
    const fields = this.mergedCustomFields?.length ? this.mergedCustomFields : this.reservation?.customFields || [];
    const values = this.reservation?.customFieldValues || [];
    if (!fields.length || !values.length) {
      return;
    }

    const selectedByField = new Map<number, Set<string>>();
    values.forEach((v) => {
      if (v?.customFieldId == null || v.value == null) {
        return;
      }
      const fieldId = Number(v.customFieldId);
      if (!selectedByField.has(fieldId)) {
        selectedByField.set(fieldId, new Set<string>());
      }
      v.value
        .toString()
        .split('|')
        .map((x) => x.trim())
        .filter((x) => !!x)
        .forEach((x) => selectedByField.get(fieldId)!.add(x));
    });

    let extrasAmount = 0;
    fields.forEach((cf) => {
      if (cf?.type !== 'DROPDOWN' || cf?.dropdownType !== 'TABLE') {
        return;
      }
      const selected = selectedByField.get(cf.id);
      if (!selected?.size) {
        return;
      }
      (cf.dropdownOptionsTable || []).forEach((opt) => {
        if (opt?.name && selected.has(opt.name)) {
          const price = Number(opt.price ?? 0);
          if (Number.isFinite(price) && price > 0) {
            extrasAmount += price;
          }
        }
      });
    });

    const prevExtras = Number(this.reservation.extrasAmount ?? 0);
    const currentTotal = Number(this.reservation.totalAmount ?? 0);
    if (!Number.isFinite(prevExtras) || !Number.isFinite(currentTotal)) {
      return;
    }
    const roundedExtras = Math.round(extrasAmount * 100) / 100;
    const delta = roundedExtras - prevExtras;
    this.reservation.extrasAmount = roundedExtras;
    this.reservation.totalAmount = Math.round((currentTotal + delta) * 100) / 100;
  }
}
